import ContactsList from '@/components/contacts-list';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type { Contact } from '@/contexts/contacts/types';
import DeleteIcon from './images/delete.svg?react';
import type { GroupAddMembersProps } from './interface';
import styles from './index.module.scss';

export default function GroupAddMembers({
  open,
  contactList,
  onCancel,
  onActionOk,
}: GroupAddMembersProps) {
  const [loading, setLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  const contacts_ = useMemo(() => {
    return contactList.filter((item) => item.name?.includes(searchText));
  }, [searchText, contactList]);

  const hasContacts = contacts_.length > 0;

  const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);

  const handleInvite = useCallback(async () => {
    setLoading(true);
    await onActionOk?.(selectedContacts).catch((_) => '');
    setLoading(false);
  }, [selectedContacts, onActionOk]);

  const onContactSelect = useCallback((contacts: Contact[]) => {
    setSelectedContacts(contacts);
  }, []);

  const onContactDelete = useCallback(
    (contact: Contact) => {
      setSelectedContacts(selectedContacts.filter((c) => c.id !== contact.id));
    },
    [selectedContacts]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedContacts.length === contacts_.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts_);
    }
  }, [contacts_, selectedContacts.length]);

  useEffect(() => {
    if (open) {
      setSelectedContacts([]);
      setSearchText('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      cancelText="Cancel"
      okText="Invite"
      title="Add Members"
      destroyOnHidden
      centered
      width={730}
      styles={{
        content: {
          padding: '24px 16px',
        },
      }}
      footer={
        <Flex justify={hasContacts ? 'space-between' : 'end'} align="center">
          {hasContacts && (
            <Checkbox
              className={styles.checkbox}
              indeterminate={
                selectedContacts.length > 0 &&
                selectedContacts.length < contacts_.length
              }
              checked={selectedContacts.length === contacts_.length}
              onChange={handleSelectAll}
            >
              Select all
            </Checkbox>
          )}
          <Flex gap={16} align="center" justify="end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleInvite}
              disabled={!selectedContacts.length}
            >
              Invite
            </Button>
          </Flex>
        </Flex>
      }
    >
      <div className={styles.groupAddMembers}>
        <div className={styles.list}>
          {hasContacts && (
            <div className={styles.search}>
              <Input
                prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                variant="filled"
                placeholder="Search"
                value={searchText}
                onChange={onSearch}
              />
            </div>
          )}
          <div className={styles.contactsWrapper}>
            <ContactsList
              contacts={contacts_}
              selectedContacts={selectedContacts}
              onContactSelect={onContactSelect}
            />
          </div>
        </div>
        <div className={styles.list}>
          {hasContacts && (
            <div className={styles.membersListTitle}>
              Selected: {selectedContacts.length}{' '}
              {selectedContacts.length > 1 ? 'Members' : 'Member'}
            </div>
          )}
          {selectedContacts.length > 0 && (
            <div className={cls(styles.selectedContactsListWrapper, 'scrollbar_hover')}>
              <div className={styles.selectedContactsList}>
                {selectedContacts.map((contact) => (
                  <Flex key={contact.id} align="center" justify="space-between">
                    <Flex align="center" justify="space-between" gap={10}>
                      <MyAvatar src={getAvatar(contact.avatar, contact.name)} />
                      <div className={styles.membersNames}>
                        <div className={styles.name}>{contact.name}</div>
                        {contact.username && (
                          <div className={styles.uname}>@{contact.username}</div>
                        )}
                      </div>
                    </Flex>
                    <DeleteIcon
                      className="pointer"
                      onClick={() => onContactDelete(contact)}
                    />
                  </Flex>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
